import { useEffect, useRef, useState } from "react";
import * as ScreenOrientation from "expo-screen-orientation";
import * as SplashScreen from "expo-splash-screen";
import { initializeLegacyKit } from "@src/config/legacyKitConfig";
import { legacyKitLifecycle } from "modules/legacy-kit";
import { useAuthStore } from "@src/features/auth";
import { initFeatureFlags, hydrateFeatureFlagsFromCache } from "@src/services/feature-flags/feature-flags";
import { initAnalytics, recordingDestination } from "@visioni/a3s-analytics";
import { amplitudeDestination } from "@visioni/a3s-analytics/react-native";
import { appVersion } from "@src/config/build";
import { amplitudeProject } from "@src/config/runtime";
import { networkReachability } from "@src/utils/network";
import { isE2ETest } from "@src/modules/LaunchArguments";
import type { UseAppReadinessReturn } from "./useAppReadiness";
import { useIsMountedRef } from "./useIsMountedRef";

/** Maximum time to wait for boot to complete before showing the app anyway. */
const BOOT_TIMEOUT_MS = 10_000;

type UseAppBootOptions = Pick<UseAppReadinessReturn, "markReady"> & {
  isTenantResolved: boolean;
  checkForceUpgrade: () => void;
};

/** Boot sequence — runs once on mount. Resolves orientation, auth and flags, then marks the app ready. */
export function useAppBoot({ markReady, isTenantResolved, checkForceUpgrade }: UseAppBootOptions): void {
  const [isBooted, setIsBooted] = useState(false);
  const isMountedRef = useIsMountedRef();
  const finalisedRef = useRef(false);

  const finaliseBoot = () => {
    if (finalisedRef.current) return;
    finalisedRef.current = true;
    SplashScreen.setOptions({ fade: true, duration: 600 });
    void SplashScreen.hideAsync();
  };

  useEffect(() => {
    const initServices = async () => {
      // Analytics starts first, so no earlier step can keep it from starting (it
      // never rejects), but boot does not wait on it (up to 10s) except where it
      // must: Experiment, in initFeatureFlags, binds to the initialised Analytics SDK.
      const analyticsReady = initAnalytics({
        destination: amplitudeDestination({ apiKey: amplitudeProject.apiKey, appVersion }),
      });
      initializeLegacyKit();
      legacyKitLifecycle.initialize();
      networkReachability.initialize();
      await analyticsReady;
      await initFeatureFlags();
    };

    const initAuth = async () => {
      await useAuthStore.getState().initialize();
      hydrateFeatureFlagsFromCache();
    };

    const boot = async () => {
      try {
        await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);

        if (isE2ETest()) {
          // E2E: events are kept in memory for the run, never sent.
          await initAnalytics({ destination: recordingDestination() });
          await initFeatureFlags();
          markReady();
          finaliseBoot();
          return;
        }

        await initServices();
        await initAuth();

        // Refetch now that the session is known, so flags target the signed-in user. If it
        // fails, boot carries on with cached or default flags: signing in needs none, and
        // AuthProvider holds a signed-in user until flags are usable.
        await initFeatureFlags();

        if (!isMountedRef.current) return;
        setIsBooted(true);
      } catch (error) {
        console.warn("App init error:", error);
        markReady();
        finaliseBoot();
      }
    };

    void boot();

    return () => {
      legacyKitLifecycle.cleanup();
      networkReachability.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safety net: if boot hasn't finalised within BOOT_TIMEOUT_MS, show the app anyway.
  // Guards against any unforeseen hung promise (e.g. a network request with no timeout).
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (finalisedRef.current) return;
      console.warn("[useAppBoot] Boot timeout — showing the app");
      markReady();
      finaliseBoot();
    }, BOOT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Guard: boot still running.
    if (!isBooted) return;
    // Guard: wait for tenant resolution gate.
    if (!isTenantResolved) return;
    // Guard: boot already finalised.
    if (finalisedRef.current) return;

    void checkForceUpgrade();
    void ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).finally(() => {
      // Guard: unmounted during async work.
      if (isMountedRef.current) markReady();
      finaliseBoot();
    });
  }, [isBooted, isTenantResolved, isMountedRef, checkForceUpgrade, markReady]);
}
